import React, { useEffect, useState } from "react";
import { Outlet, NavLink, useNavigate } from "react-router-dom";
import {
  LayoutDashboard, Brain, CalendarDays, Video, LineChart,
  Sparkles, GitBranch, LogOut, Zap, Link2, CreditCard, ShieldCheck, Film
} from "lucide-react";
import { useAuth } from "@/lib/auth";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";

const NAV = [
  { to: "/app", label: "Command", icon: LayoutDashboard, end: true, testid: "nav-dashboard" },
  { to: "/app/brand", label: "Brand Intelligence", icon: Brain, testid: "nav-brand" },
  { to: "/app/plan", label: "Weekly Plan", icon: CalendarDays, testid: "nav-plan" },
  { to: "/app/film", label: "Film This", icon: Video, testid: "nav-film" },
  { to: "/app/performance", label: "Performance", icon: LineChart, testid: "nav-performance" },
  { to: "/app/review", label: "Weekly Review", icon: Sparkles, testid: "nav-review" },
  { to: "/app/belief", label: "Belief Journeys", icon: GitBranch, testid: "nav-belief" },
  { to: "/app/studio", label: "ViralCut Studio", icon: Film, testid: "nav-studio" },
  { to: "/app/connections", label: "Connections", icon: Link2, testid: "nav-connections" },
  { to: "/app/billing", label: "Billing", icon: CreditCard, testid: "nav-billing" },
];

export default function AppShell() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [tier, setTier] = useState(null);

  // Force dark theme for the whole authenticated app
  useEffect(() => {
    document.documentElement.classList.add("dark");
  }, []);

  // Load current subscription tier for the sidebar badge
  useEffect(() => {
    api.get("/api/billing/subscription")
      .then((r) => setTier(r.data?.tier || null))
      .catch(() => setTier(null));
  }, []);

  return (
    <div className="min-h-screen flex bg-background text-foreground">
      <aside className="w-64 shrink-0 border-r border-border bg-card min-h-screen sticky top-0 flex flex-col">
        <button
          onClick={() => nav("/app")}
          data-testid="sidebar-logo"
          className="flex items-center gap-2 px-6 pt-6 pb-8 text-left"
        >
          <div className="h-8 w-8 grid place-items-center bg-primary text-primary-foreground rounded">
            <Zap className="h-4 w-4" strokeWidth={2.5}/>
          </div>
          <div className="font-display font-black text-xl tracking-tight">ContentOS</div>
        </button>

        <nav className="px-3 space-y-0.5 flex-1">
          {NAV.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.end}
              data-testid={n.testid}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2 rounded text-sm transition-colors duration-200 ${
                  isActive
                    ? "bg-foreground text-background font-semibold"
                    : "text-muted-foreground hover:bg-secondary hover:text-foreground"
                }`
              }
            >
              <n.icon className="h-4 w-4" />
              <span>{n.label}</span>
            </NavLink>
          ))}
          {user?.is_admin && (
            <NavLink
              to="/app/admin"
              data-testid="nav-admin"
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2 rounded text-sm transition-colors duration-200 ${
                  isActive
                    ? "bg-primary text-primary-foreground font-semibold"
                    : "text-primary hover:bg-primary/10"
                }`
              }
            >
              <ShieldCheck className="h-4 w-4" />
              <span>Admin</span>
            </NavLink>
          )}
        </nav>

        <div className="border-t border-border p-4">
          <div className="flex items-center justify-between">
            <div className="text-xs uppercase tracking-widest text-muted-foreground">Signed in</div>
            {tier && (
              <span
                data-testid="sidebar-plan-badge"
                className="inline-flex items-center rounded-full border border-primary bg-primary/10 px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest text-primary"
              >
                {tier}
              </span>
            )}
          </div>
          <div className="mt-1 font-semibold truncate" data-testid="sidebar-user-name">{user?.name}</div>
          <div className="text-xs text-muted-foreground truncate">{user?.email}</div>
          <Button
            variant="outline"
            size="sm"
            className="mt-3 w-full"
            data-testid="btn-logout"
            onClick={logout}
          >
            <LogOut className="h-3.5 w-3.5" /> Log out
          </Button>
        </div>
      </aside>

      <main className="flex-1 min-w-0">
        <Outlet />
      </main>
    </div>
  );
}
